/**
 * P45 UI primitives.
 *
 * Pages should compose pages out of these — they bake the colour
 * tokens, typography weights, spacing, and number alignment that
 * make the app feel like a Platform45 internal tool rather than a
 * generic dashboard.
 *
 * Tokens flow from src/index.css via Tailwind colour mappings, so
 * the same classes here switch automatically between the light
 * default and the dark variant (toggled by `html.dark`).
 */

import { Info, ChevronUp, ChevronDown, ChevronsUpDown, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

/* ─── Page header ──────────────────────────────────────────────────────── */

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <header className="flex items-end justify-between gap-6 mb-8 pb-6 border-b border-slate-700">
      <div>
        {eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">
            {eyebrow}
          </p>
        )}
        <h1 className="text-3xl font-semibold text-slate-100">{title}</h1>
        {subtitle && (
          <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </header>
  );
}

/* ─── Card ─────────────────────────────────────────────────────────────── */

export function Card({ title, subtitle, action, padding = 'p-6', children, className = '' }) {
  return (
    <section className={`bg-slate-800 border border-slate-700 rounded-xl ${className}`}>
      {(title || action) && (
        <header className="flex items-end justify-between px-6 pt-5 pb-4 border-b border-slate-700">
          <div>
            {title && (
              <h2 className="text-sm font-semibold text-slate-100 tracking-tight">{title}</h2>
            )}
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      <div className={padding}>{children}</div>
    </section>
  );
}

/* ─── Stat tile ────────────────────────────────────────────────────────── */

export function Stat({ label, value, sub, trend, loading, info }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500 flex items-center gap-1">
        {label}
        {info && <InfoDot>{info}</InfoDot>}
      </p>
      {loading ? (
        <div className="h-8 w-24 bg-slate-700 rounded mt-3 animate-pulse" />
      ) : (
        <p className="text-3xl font-semibold text-slate-100 nums mt-2 tracking-tight">{value}</p>
      )}
      <div className="flex items-baseline gap-2 mt-1.5 text-xs">
        {sub && <span className="text-slate-500">{sub}</span>}
        {trend != null && (
          <span className={trend >= 0 ? 'text-emerald-600' : 'text-brand-500'}>
            {trend >= 0 ? '+' : ''}
            {trend.toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Button ───────────────────────────────────────────────────────────── */

export function Button({
  variant = 'primary',
  size = 'md',
  disabled,
  loading,
  children,
  className = '',
  ...rest
}) {
  const base =
    'inline-flex items-center justify-center gap-2 font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const sizes = {
    sm: 'text-xs px-3 py-1.5',
    md: 'text-sm px-4 py-2',
    lg: 'text-sm px-5 py-2.5',
  };
  const variants = {
    primary:   'bg-brand-500 text-white hover:bg-brand-600',
    secondary: 'bg-slate-800 text-slate-100 border border-slate-700 hover:bg-slate-700',
    ghost:     'text-slate-500 hover:text-slate-100',
    danger:    'bg-brand-500 text-white hover:bg-brand-600',
  };
  return (
    <button
      disabled={disabled || loading}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
      {...rest}
    >
      {loading ? '…' : children}
    </button>
  );
}

/* ─── Pill (status / tag) ──────────────────────────────────────────────── */

export function Pill({ tone = 'neutral', children, className = '' }) {
  const tones = {
    neutral: 'bg-slate-700 text-slate-300',
    accent:  'bg-brand-500/10 text-brand-500',
    warn:    'bg-amber-500/10 text-amber-600',
    ok:      'bg-emerald-500/10 text-emerald-600',
    danger:  'bg-brand-500/10 text-brand-500',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium tracking-tight ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/* ─── Table primitives ─────────────────────────────────────────────────── */

export function Table({ children, className = '', maxHeight }) {
  // With maxHeight the wrapper becomes the single scroll container (both
  // axes), so sticky headers / pinned total rows inside stick correctly.
  // Without it, only horizontal overflow scrolls (default behaviour).
  return (
    <div className={maxHeight ? `overflow-auto ${maxHeight}` : 'overflow-x-auto'}>
      <table className={`w-full text-sm border-separate border-spacing-0 ${className}`}>
        {children}
      </table>
    </div>
  );
}

export function THead({ children }) {
  return (
    <thead>
      <tr className="text-[11px] font-medium uppercase tracking-[0.10em] text-slate-500">
        {children}
      </tr>
    </thead>
  );
}

export function TH({ align = 'left', children, className = '' }) {
  return (
    <th
      className={`px-3 py-2.5 border-b border-slate-700 text-${align} font-medium ${className}`}
    >
      {children}
    </th>
  );
}

export function TR({ children, className = '', ...rest }) {
  return (
    <tr className={`group ${className}`} {...rest}>
      {children}
    </tr>
  );
}

export function TD({ align = 'left', mono, muted, children, className = '' }) {
  return (
    <td
      className={`px-3 py-2.5 border-b border-slate-700/60 text-${align} ${mono ? 'nums' : ''} ${
        muted ? 'text-slate-400' : 'text-slate-200'
      } ${className}`}
    >
      {children}
    </td>
  );
}

/* ─── Inline field ─────────────────────────────────────────────────────── */

export function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
        {label}
      </span>
      {children}
      {hint && <span className="text-[11px] text-slate-500">{hint}</span>}
    </label>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-brand-500/20 transition-colors ${
        props.className || ''
      }`}
    />
  );
}

export function Select(props) {
  return (
    <select
      {...props}
      className={`bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-brand-500/20 ${
        props.className || ''
      }`}
    />
  );
}

/* ─── Empty + tooltip ──────────────────────────────────────────────────── */

export function Empty({ children = 'No data.' }) {
  return (
    <div className="py-12 text-center text-sm text-slate-500">{children}</div>
  );
}

export function Hint({ children, label }) {
  return (
    <span className="inline-flex items-center gap-1 group relative cursor-help">
      <span>{label}</span>
      <Info className="w-3 h-3 text-slate-500 group-hover:text-slate-300 transition-colors" />
      <span className="pointer-events-none absolute left-0 bottom-full mb-2 z-50 w-60 rounded-md bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-200 normal-case tracking-normal font-normal leading-relaxed shadow-xl opacity-0 group-hover:opacity-100 transition-opacity">
        {children}
      </span>
    </span>
  );
}

/* Icon-only info bubble — drop next to a tile label or table header.
   Tooltip opens below so it never clips above a card's top edge. */
export function InfoDot({ children, w = 'w-64' }) {
  return (
    <span className="inline-flex group relative cursor-help align-middle">
      <Info className="w-3 h-3 text-slate-500 group-hover:text-slate-300 transition-colors" />
      <span className={`pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 ${w} rounded-md bg-slate-900 border border-slate-700 px-3 py-2 text-xs text-slate-200 normal-case tracking-normal font-normal leading-relaxed shadow-xl opacity-0 group-hover:opacity-100 transition-opacity`}>
        {children}
      </span>
    </span>
  );
}

/* Provenance banner — explains, at the top of a page, which BambooHR /
   Magnetic / app fields feed the numbers below. `items` is an array of
   { label, source, detail } rows. */
export function SourceNote({ title = 'Where these numbers come from', items = [], className = '' }) {
  const tone = {
    Magnetic: 'accent', BambooHR: 'ok', Bamboo: 'ok', App: 'neutral', Derived: 'warn',
  };
  return (
    <div className={`bg-slate-800/60 border border-slate-700 rounded-xl p-4 mb-6 ${className}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-3 flex items-center gap-1">
        <Info className="w-3 h-3" /> {title}
      </p>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-slate-400 leading-relaxed flex gap-2">
            <Pill tone={tone[it.source] || 'neutral'} className="shrink-0">{it.source}</Pill>
            <span><span className="text-slate-200 font-medium">{it.label}</span>{it.detail ? ` — ${it.detail}` : ''}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─── DataTable ────────────────────────────────────────────────────────────
   Sortable + filterable table.

   columns: [{ key, label, align?, mono?, muted?, sortable?, filter?, info?,
               render?(row) → ReactNode, accessor?(row) → primitive }]
   - sortable defaults to true. accessor is the value used for sort/filter.
   - filter: 'text' (default, contains) | 'select' (auto-derived enum)
             | false to hide a per-column filter.
   - Pass `global` to show a top-of-table search box across all sortables.
*/
export function DataTable({ columns, rows, initialSort, empty = 'No data.', global = true, className = '' }) {
  const [sort, setSort] = useState(initialSort || null);  // { key, dir }
  const [colFilters, setColFilters] = useState({});       // { key: 'text' }
  const [globalQ, setGlobalQ] = useState('');

  const acc = (row, col) => (col.accessor ? col.accessor(row) : row[col.key]);
  const toStr = (v) => (v == null ? '' : String(v));
  const num = (v) => (typeof v === 'number' ? v : Number(String(v).replace(/[^\d.-]/g, '')) || null);

  const filtered = useMemo(() => {
    const q = globalQ.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const hit = columns.some((c) => toStr(acc(r, c)).toLowerCase().includes(q));
        if (!hit) return false;
      }
      for (const c of columns) {
        const f = (colFilters[c.key] || '').trim().toLowerCase();
        if (!f) continue;
        if (!toStr(acc(r, c)).toLowerCase().includes(f)) return false;
      }
      return true;
    });
  }, [rows, columns, colFilters, globalQ]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const c = columns.find((x) => x.key === sort.key); if (!c) return filtered;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = acc(a, c), bv = acc(b, c);
      const an = num(av), bn = num(bv);
      if (an != null && bn != null) return (an - bn) * dir;
      return toStr(av).localeCompare(toStr(bv), undefined, { numeric: true }) * dir;
    });
  }, [filtered, sort, columns]);

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setSort((s) => !s || s.key !== col.key ? { key: col.key, dir: 'asc' }
                : s.dir === 'asc' ? { key: col.key, dir: 'desc' }
                : null);
  };

  // build select-filter options when filter='select'
  const selectOpts = useMemo(() => {
    const out = {};
    for (const c of columns) {
      if (c.filter === 'select') {
        out[c.key] = Array.from(new Set(rows.map((r) => toStr(acc(r, c))).filter(Boolean))).sort();
      }
    }
    return out;
  }, [rows, columns]);

  return (
    <div className={className}>
      {global && (
        <div className="px-4 py-3 border-b border-slate-700 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-500" />
          <input value={globalQ} onChange={(e) => setGlobalQ(e.target.value)} placeholder="Search…"
            className="flex-1 bg-transparent text-sm text-slate-100 placeholder-slate-500 focus:outline-none" />
          {globalQ && <button onClick={() => setGlobalQ('')} className="text-slate-500 hover:text-slate-200"><X className="w-3.5 h-3.5" /></button>}
          <span className="text-[11px] text-slate-500">{sorted.length} / {rows.length}</span>
        </div>
      )}
      <Table>
        <thead>
          <tr className="text-[11px] font-medium uppercase tracking-[0.10em] text-slate-500">
            {columns.map((c) => {
              const active = sort?.key === c.key;
              const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ChevronUp : ChevronDown;
              return (
                <th key={c.key} className={`px-3 py-2.5 border-b border-slate-700 text-${c.align || 'left'} font-medium`}>
                  <button type="button" onClick={() => toggleSort(c)}
                    className={`inline-flex items-center gap-1 ${c.sortable === false ? 'cursor-default' : 'hover:text-slate-200 cursor-pointer'}`}>
                    <span>{c.label}</span>
                    {c.info && <InfoDot>{c.info}</InfoDot>}
                    {c.sortable !== false && <Icon className={`w-3 h-3 ${active ? 'text-brand-500' : 'text-slate-600'}`} />}
                  </button>
                </th>
              );
            })}
          </tr>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="px-3 pb-2 border-b border-slate-700/60">
                {c.filter === false ? null : c.filter === 'select' ? (
                  <select value={colFilters[c.key] || ''}
                    onChange={(e) => setColFilters({ ...colFilters, [c.key]: e.target.value })}
                    className="w-full text-xs bg-slate-900 border border-slate-700 rounded px-2 py-1 text-slate-200">
                    <option value="">All</option>
                    {(selectOpts[c.key] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input value={colFilters[c.key] || ''}
                    onChange={(e) => setColFilters({ ...colFilters, [c.key]: e.target.value })}
                    placeholder="filter"
                    className="w-full text-xs bg-slate-900 border border-slate-700 rounded px-2 py-1 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-slate-500" />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan={columns.length} className="py-10 text-center text-sm text-slate-500">{empty}</td></tr>
          ) : sorted.map((row, i) => (
            <tr key={i} className="group">
              {columns.map((c) => (
                <td key={c.key}
                  className={`px-3 py-2.5 border-b border-slate-700/60 text-${c.align || 'left'} ${c.mono ? 'nums' : ''} ${c.muted ? 'text-slate-400' : 'text-slate-200'}`}>
                  {c.render ? c.render(row) : (acc(row, c) ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

/* ─── Number formatters ────────────────────────────────────────────────── */

export const fmtR = (v) =>
  `R ${Number(v || 0).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;

export const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;

export const fmtH = (v) => `${Number(v || 0).toFixed(1)} h`;

export const fmtDate = (v) =>
  v ? new Date(v).toISOString().slice(0, 10) : '—';

export const fmtDT = (v) =>
  v
    ? new Date(v).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })
    : '—';

export const todayISO = () => new Date().toISOString().slice(0, 10);

export const thisMonthISO = () => new Date().toISOString().slice(0, 7);
